import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  authenticate,
  COGNITO_SESSION_MAX_AGE_MS,
  CognitoRequestError,
  CognitoTokens,
  cognitoStorageKey,
  completeNewPassword,
  getProfile,
  getTokenGroups,
  getTokenExpiration,
  globalSignOut,
  isCognitoSessionExpired,
  MemberProfile,
  NewPasswordRequiredError,
  refreshAuthentication,
  updateProfile as updateCognitoProfile,
} from './cognito';
import {
  AdminMember,
  AdminMemberOrders,
  cancelMemberService,
  createMemberProposal as createAdminMemberProposal,
  createMemberProposalDownload as createAdminMemberProposalDownload,
  deleteMember as deleteAdminMember,
  ensureMemberProfile,
  listMembers as listAdminMembers,
  listAdminOrders,
  listMemberOrders,
  listMemberMeetings,
  cancelMemberMeeting,
  MemberMeeting,
  MemberOrder,
  ProposalDownloadResponse,
  updateMemberProfile,
  uploadMemberProposalFiles as uploadAdminMemberProposalFiles,
} from './memberApi';
import { formatStoredUsPhoneForInput } from './phone';

const emptyMemberAddress = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
};

const readMemberAddress = (value: unknown) => {
  if (!value || typeof value !== 'object') {
    return { ...emptyMemberAddress };
  }
  const address = value as Record<string, unknown>;
  return {
    addressLine1: String(address.addressLine1 || ''),
    addressLine2: String(address.addressLine2 || ''),
    city: String(address.city || ''),
    state: String(address.state || ''),
    zipCode: String(address.zipCode || ''),
  };
};

interface AuthContextValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  profile: MemberProfile | null;
  login: (email: string, password: string) => Promise<void>;
  completeNewPasswordLogin: (
    email: string,
    temporaryPassword: string,
    newPassword: string,
    session: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (
    profile: Pick<MemberProfile, 'name' | 'companyName' | 'website' | 'phoneNumber' | 'emailOptIn' | 'isReseller' | 'shippingAddress' | 'billingAddress' | 'billingSameAsShipping' | 'supportNotes'>,
  ) => Promise<void>;
  listMembers: () => Promise<AdminMember[]>;
  listAdminOrders: () => Promise<AdminMemberOrders[]>;
  listOrders: () => Promise<MemberOrder[]>;
  listMeetings: () => Promise<MemberMeeting[]>;
  cancelMeeting: (bookingUid: string, action: 'cancel' | 'reschedule') => Promise<void>;
  createMemberProposal: (memberId: string, proposalName: string) => Promise<AdminMember>;
  createMemberProposalDownload: (
    memberId: string,
    proposalId: string,
    email?: boolean,
    existingDownload?: ProposalDownloadResponse,
  ) => Promise<ProposalDownloadResponse>;
  uploadMemberProposalFiles: (
    memberId: string,
    proposalId: string,
    files: File[],
  ) => Promise<void>;
  cancelService: (confirmation: string) => Promise<void>;
  deleteMember: (memberId: string, confirmation: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const readStoredTokens = (): CognitoTokens | null => {
  try {
    const value = localStorage.getItem(cognitoStorageKey);
    if (!value) {
      return null;
    }
    const session = JSON.parse(value) as CognitoTokens;
    if (isCognitoSessionExpired(session)) {
      localStorage.removeItem(cognitoStorageKey);
      return null;
    }
    return session;
  } catch {
    localStorage.removeItem(cognitoStorageKey);
    return null;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [tokens, setTokens] = useState<CognitoTokens | null>(readStoredTokens);
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const storeTokens = useCallback((nextTokens: CognitoTokens | null) => {
    setTokens(nextTokens);
    if (nextTokens) {
      localStorage.setItem(cognitoStorageKey, JSON.stringify(nextTokens));
    } else {
      localStorage.removeItem(cognitoStorageKey);
    }
  }, []);

  const loadProfile = useCallback(async (session: CognitoTokens) => {
    if (isCognitoSessionExpired(session)) {
      throw new Error('Your 24-hour session has expired. Please log in again.');
    }
    let activeSession = session;
    if (getTokenExpiration(session.accessToken) <= Date.now() + 30_000) {
      if (!session.refreshToken) {
        throw new Error('Your session has expired.');
      }
      activeSession = await refreshAuthentication(
        session.refreshToken,
        session.sessionStartedAt,
      );
      storeTokens(activeSession);
    }

    const cognitoProfile = await getProfile(activeSession.accessToken);
    const memberResponse = await ensureMemberProfile(activeSession.accessToken, cognitoProfile);
    const member = memberResponse.member || {};
    setProfile({
      ...cognitoProfile,
      memberId: String(member.memberId || cognitoProfile.memberId),
      name: String(member.name || cognitoProfile.name || ''),
      companyName: String(member.companyName || ''),
      website: String(member.website || ''),
      phoneNumber: formatStoredUsPhoneForInput(String(member.phone || cognitoProfile.phoneNumber || '')),
      emailOptIn: member.emailOptIn !== false,
      isReseller: member.isReseller === true,
      shippingAddress: readMemberAddress(member.shippingAddress),
      billingAddress: readMemberAddress(member.billingAddress),
      billingSameAsShipping: member.billingSameAsShipping === true,
      emailVerified: cognitoProfile.emailVerified || member.emailVerified === true,
      status: String(member.status || 'ACTIVE'),
      supportNotes: String(member.supportNotes || ''),
      serviceCancelledOn: String(member.ServiceCancelledOn || ''),
      groups: getTokenGroups(activeSession.accessToken),
    });
    return activeSession;
  }, [storeTokens]);

  useEffect(() => {
    if (!tokens) {
      return undefined;
    }

    const expiresAt = tokens.sessionStartedAt + COGNITO_SESSION_MAX_AGE_MS;
    const remaining = expiresAt - Date.now();
    const expireSession = () => {
      globalSignOut(tokens.accessToken).catch(() => undefined);
      storeTokens(null);
      setProfile(null);
    };

    if (remaining <= 0) {
      expireSession();
      return undefined;
    }

    const timeout = window.setTimeout(expireSession, remaining);
    return () => window.clearTimeout(timeout);
  }, [storeTokens, tokens]);

  useEffect(() => {
    const restoreSession = async () => {
      if (!tokens) {
        setIsLoading(false);
        return;
      }

      try {
        await loadProfile(tokens);
      } catch {
        storeTokens(null);
        setProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
    // Restore once from local storage; later token changes are managed by auth actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const nextTokens = await authenticate(email, password);
    storeTokens(nextTokens);
    try {
      await loadProfile(nextTokens);
    } catch (error) {
      storeTokens(null);
      setProfile(null);
      throw error;
    }
  }, [loadProfile, storeTokens]);

  const completeNewPasswordLogin = useCallback(async (
    email: string,
    temporaryPassword: string,
    newPassword: string,
    session: string,
  ) => {
    let nextTokens: CognitoTokens;
    try {
      nextTokens = await completeNewPassword(email, newPassword, session);
    } catch (error) {
      const isExpiredSession = (
        error instanceof CognitoRequestError
        && /invalid session|session is expired/i.test(error.message)
      );
      if (!isExpiredSession) {
        throw error;
      }

      try {
        nextTokens = await authenticate(email, temporaryPassword);
      } catch (refreshError) {
        if (refreshError instanceof NewPasswordRequiredError) {
          nextTokens = await completeNewPassword(
            refreshError.email,
            newPassword,
            refreshError.session,
          );
        } else {
          throw refreshError;
        }
      }
    }
    storeTokens(nextTokens);
    try {
      await loadProfile(nextTokens);
    } catch (error) {
      storeTokens(null);
      setProfile(null);
      throw error;
    }
  }, [loadProfile, storeTokens]);

  const logout = useCallback(async () => {
    if (tokens?.accessToken) {
      try {
        await globalSignOut(tokens.accessToken);
      } catch {
        // Local logout must still succeed when the remote session is already expired.
      }
    }
    storeTokens(null);
    setProfile(null);
  }, [storeTokens, tokens]);

  const updateProfile = useCallback(async (
    values: Pick<MemberProfile, 'name' | 'companyName' | 'website' | 'phoneNumber' | 'emailOptIn' | 'isReseller' | 'shippingAddress' | 'billingAddress' | 'billingSameAsShipping' | 'supportNotes'>,
  ) => {
    if (!tokens) {
      throw new Error('You must be logged in to update your profile.');
    }
    const activeSession = await loadProfile(tokens);
    await updateCognitoProfile(activeSession.accessToken, {
      name: values.name,
      phoneNumber: values.phoneNumber,
    });
    await updateMemberProfile(activeSession.accessToken, values);
    await loadProfile(activeSession);
  }, [loadProfile, tokens]);

  const cancelService = useCallback(async (confirmation: string) => {
    if (!tokens) {
      throw new Error('You must be logged in to cancel your service.');
    }
    const activeSession = await loadProfile(tokens);
    await cancelMemberService(activeSession.accessToken, confirmation);
    storeTokens(null);
    setProfile(null);
  }, [loadProfile, storeTokens, tokens]);

  const listMembers = useCallback(async () => {
    if (!tokens) {
      throw new Error('You must be logged in to view members.');
    }
    const activeSession = await loadProfile(tokens);
    const response = await listAdminMembers(activeSession.accessToken);
    return response.members;
  }, [loadProfile, tokens]);

  const listOrders = useCallback(async () => {
    if (!tokens) {
      throw new Error('You must be logged in to view orders.');
    }
    const activeSession = await loadProfile(tokens);
    const response = await listMemberOrders(activeSession.accessToken);
    return response.orders;
  }, [loadProfile, tokens]);

  const listMeetings = useCallback(async () => {
    if (!tokens) {
      throw new Error('You must be logged in to view meetings.');
    }
    const activeSession = await loadProfile(tokens);
    const response = await listMemberMeetings(activeSession.accessToken);
    return response.meetings;
  }, [loadProfile, tokens]);

  const cancelMeeting = useCallback(async (
    bookingUid: string,
    action: 'cancel' | 'reschedule',
  ) => {
    if (!tokens) {
      throw new Error('You must be logged in to manage meetings.');
    }
    const activeSession = await loadProfile(tokens);
    await cancelMemberMeeting(activeSession.accessToken, bookingUid, action);
  }, [loadProfile, tokens]);

  const listOrdersForAdmin = useCallback(async () => {
    if (!tokens) {
      throw new Error('You must be logged in to view orders.');
    }
    const activeSession = await loadProfile(tokens);
    const response = await listAdminOrders(activeSession.accessToken);
    return response.memberOrders;
  }, [loadProfile, tokens]);

  const createMemberProposal = useCallback(async (memberId: string, proposalName: string) => {
    if (!tokens) {
      throw new Error('You must be logged in to create proposals.');
    }
    const activeSession = await loadProfile(tokens);
    const response = await createAdminMemberProposal(activeSession.accessToken, memberId, proposalName);
    return response.member;
  }, [loadProfile, tokens]);

  const createMemberProposalDownload = useCallback(async (
    memberId: string,
    proposalId: string,
    email = false,
    existingDownload?: ProposalDownloadResponse,
  ) => {
    if (!tokens) {
      throw new Error('You must be logged in to create proposal downloads.');
    }
    const activeSession = await loadProfile(tokens);
    return createAdminMemberProposalDownload(
      activeSession.accessToken,
      memberId,
      proposalId,
      email,
      existingDownload,
    );
  }, [loadProfile, tokens]);

  const uploadMemberProposalFiles = useCallback(async (
    memberId: string,
    proposalId: string,
    files: File[],
  ) => {
    if (!tokens) {
      throw new Error('You must be logged in to upload proposal files.');
    }
    const activeSession = await loadProfile(tokens);
    await uploadAdminMemberProposalFiles(activeSession.accessToken, memberId, proposalId, files);
  }, [loadProfile, tokens]);

  const deleteMember = useCallback(async (memberId: string, confirmation: string) => {
    if (!tokens) {
      throw new Error('You must be logged in to delete members.');
    }
    const activeSession = await loadProfile(tokens);
    await deleteAdminMember(activeSession.accessToken, memberId, confirmation);
  }, [loadProfile, tokens]);

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isAuthenticated: Boolean(tokens && profile),
        profile,
        login,
        completeNewPasswordLogin,
        logout,
        updateProfile,
        listMembers,
        listAdminOrders: listOrdersForAdmin,
        listOrders,
        listMeetings,
        cancelMeeting,
        createMemberProposal,
        createMemberProposalDownload,
        uploadMemberProposalFiles,
        cancelService,
        deleteMember,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
